import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Chip,
  useTheme,
  alpha,
  IconButton,
  Tooltip,
  Button,
  DialogActions,
  Dialog,
  DialogTitle,
  DialogContent,
  RadioGroup,
  FormControlLabel,
  Backdrop,
  CircularProgress,
  Radio,
  Drawer,
  Divider,
} from '@mui/material';
import {
  ArrowLeft,
  Clock,
  MessageSquare,
  Phone,
  Mail,
  Info,
  Trash2,
  CreditCard,
  Eye,
  Download,
  Send,
  X,
  RotateCcw,
  Video,
  Copy,
  ExternalLink,
} from 'lucide-react';
import { format } from 'date-fns';
import { Booking } from '../../types/booking';
import ChatHistory from '../../components/chat/ChatHistory';
import { ROUTES } from '../../config/constants/routes';
import { useBookingActions } from '../../hooks/booking/useBookingActions';
import { useSelector } from 'react-redux';
import { RootState } from '../../store';
import { paymentEndpoints } from '../../services/api/endpoints/payment.endpoint';
import type { Invoice } from '../../types/payment';
import { formatCurrency } from '../../utils/formatters';
import { useEffect } from 'react';
import { RefundDialog } from '../../components/payment/RefundDialog';

interface InfoCardProps {
  icon: React.ReactNode;
  title: string;
  subtitle?: string;
  children: React.ReactNode;
}

const InfoCard: React.FC<InfoCardProps> = ({ icon, title, subtitle, children }) => {
  const theme = useTheme();

  return (
    <Paper
      elevation={0}
      sx={{
        p: 2.5,
        height: '100%',
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 2,
        transition: 'all 0.2s ease-in-out',
        '&:hover': {
          borderColor: theme.palette.primary.main,
          boxShadow: `0 0 0 1px ${theme.palette.primary.main}25`,
        },
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          mb: 2,
        }}
      >
        <Box
          sx={{
            backgroundColor: alpha(theme.palette.primary.main, 0.08),
            borderRadius: 1.5,
            p: 1,
            display: 'flex',
          }}
        >
          {icon}
        </Box>
        <Box>
          <Typography variant="subtitle1" sx={{ fontWeight: 500, fontSize: '0.9375rem' }}>
            {title}
          </Typography>
          {subtitle && (
            <Typography variant="caption" color="text.secondary">
              {subtitle}
            </Typography>
          )}
        </Box>
      </Box>
      {children}
    </Paper>
  );
};

const InfoItem = ({
  icon: Icon,
  label,
  value,
}: {
  icon: React.ElementType;
  label: string;
  value: string | null;
}) => {
  const theme = useTheme();
  return (
    <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 2 }}>
      <Box
        sx={{
          mr: 2,
          color: theme.palette.primary.main,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          p: 1,
          borderRadius: 1,
          backgroundColor: `${theme.palette.primary.main}15`,
        }}
      >
        <Icon size={16} />
      </Box>
      <Box>
        <Typography variant="caption" color="text.secondary" display="block">
          {label}
        </Typography>
        <Typography variant="body2">{value || 'Not provided'}</Typography>
      </Box>
    </Box>
  );
};

interface BookingDetailProps {
  booking: Booking;
  onDelete?: (bookingId: number) => void;
}

const BookingDetail: React.FC<BookingDetailProps> = ({ booking, onDelete }) => {
  const theme = useTheme();
  const navigate = useNavigate();

  const [recurrenceDeleteDialogOpen, setRecurrenceDeleteDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteOption, setDeleteOption] = useState<'single' | 'following' | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  // Payment-related state
  const [invoice, setInvoice] = useState<Invoice | null>(null);
  const [invoiceLoading, setInvoiceLoading] = useState(false);
  const [resendProcessing, setResendProcessing] = useState(false);
  const [downloadProcessing, setDownloadProcessing] = useState(false);
  const [invoiceDetailOpen, setInvoiceDetailOpen] = useState(false);
  const [refundDialogOpen, setRefundDialogOpen] = useState(false);

  const organization = useSelector((state: RootState) => state.organizations.selectedOrganisation);
  const isStripeEnabled = organization?.isStripeEnabled ?? false;

  const { deleteRecurrenceBooking, deleteBooking } = useBookingActions();

  // Fetch invoice if Stripe is enabled and booking exists
  useEffect(() => {
    const fetchInvoice = async () => {
      if (!isStripeEnabled || !organization || !booking.id) return;

      try {
        setInvoiceLoading(true);
        const result = await paymentEndpoints.getInvoiceByBooking(
          organization.id,
          booking.id.toString()
        );
        setInvoice(result.data);
      } catch (err) {
        // No invoice found - this is OK, not all bookings require payment
        console.log('No invoice found for booking:', err);
      } finally {
        setInvoiceLoading(false);
      }
    };

    fetchInvoice();
  }, [booking.id, organization, isStripeEnabled]);

  if (!booking) return <div />;
  const services = booking.bookingServiceMapping;

  // Find the main service (isAddonService === false)
  const mainService = services.find(s => !s.services.isAddonService) || null;

  // All addon services (isAddonService === true)
  const addonService = services.filter(s => s.services.isAddonService);

  const handleResendInvoice = async () => {
    if (!invoice || !organization) return;

    try {
      setResendProcessing(true);

      // First get a checkout URL by creating a payment session
      const session = await paymentEndpoints.createPaymentSession(
        organization.id,
        invoice.id.toString()
      );

      const payload = {
        orgId: invoice.orgId,
        invoiceId: invoice.id,
        invoiceNumber: invoice.invoiceNumber,
        customerId: invoice.customerId,
        checkoutUrl: session.data.checkoutUrl,
        totalCents: invoice.totalCents,
        currency: invoice.currency,
        email: invoice.customer?.email || booking.customer.email || '',
        customerName: invoice.customer
          ? `${invoice.customer.firstName} ${invoice.customer.lastName || ''}`.trim()
          : `${booking.customer.firstName} ${booking.customer.lastName || ''}`.trim(),
        phone: invoice.customer?.phone || booking.customer.phone || '',
      };

      await paymentEndpoints.resendInvoice(organization.id, payload);
      alert('Invoice resent successfully');
    } catch (error: any) {
      alert(error.message || 'Failed to resend invoice');
    } finally {
      setResendProcessing(false);
    }
  };

  const handleViewInvoice = () => {
    setInvoiceDetailOpen(true);
  };

  const handleDownloadInvoice = async () => {
    if (!invoice || !organization) return;

    try {
      setDownloadProcessing(true);
      const response = await paymentEndpoints.downloadInvoice(
        organization.id,
        invoice.id.toString()
      );
      window.open(response.data.urls.pdf, '_blank');
    } catch (error: any) {
      alert(error.message || 'Failed to download invoice');
    } finally {
      setDownloadProcessing(false);
    }
  };

  const getInvoiceStatusColor = (status: string) => {
    switch (status) {
      case 'PAID':
        return 'success';
      case 'UNPAID':
        return 'warning';
      case 'REFUNDED':
      case 'PARTIAL_REFUNDED':
        return 'info';
      case 'CANCELLED':
      case 'FAILED':
        return 'error';
      default:
        return 'default';
    }
  };

  const handleDeleteClick = () => {
    if (booking.recurrenceId) {
      // Show recurrence options
      setRecurrenceDeleteDialogOpen(true);
    } else {
      // Show basic confirmation
      setDeleteDialogOpen(true);
    }
  };

  const confirmAndDelete = async () => {
    setIsLoading(true);
    try {
      if (booking.recurrenceId && deleteOption === 'following') {
        await deleteRecurrenceBooking(booking.recurrenceId, booking.id); // delete this and following
      } else {
        await deleteBooking(booking.id); // single delete
      }

      onDelete?.(booking.id);
      navigate(ROUTES.BOOKINGS);
    } catch (err) {
      console.error('Failed to delete booking', err);
    } finally {
      setIsLoading(false);
      setDeleteOption(null); // reset
    }
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <>
      {/* <Box sx={{}} className="2xl:absolute 2xl:top 2xl:left"> */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Tooltip title="Back to Bookings">
          <IconButton
            onClick={() => navigate(ROUTES.BOOKINGS)}
            sx={{
              mb: 2,
              backgroundColor: alpha(theme.palette.primary.main, 0.08),
              '&:hover': {
                backgroundColor: alpha(theme.palette.primary.main, 0.15),
              },
            }}
          >
            <ArrowLeft size={18} />
          </IconButton>
        </Tooltip>

        {new Date(booking.startTime) > new Date() && (
          <Button
            variant="outlined"
            color="error"
            startIcon={<Trash2 size={18} />}
            onClick={handleDeleteClick}
            sx={{
              mb: 2,
              textTransform: 'none',
              borderRadius: 2,
              px: 2,
              py: 1,
            }}
          >
            Delete Booking
          </Button>
        )}
      </Box>
      <Paper
        elevation={0}
        sx={{
          p: 0,
          backgroundColor: 'theme.palette.background.paper',
          borderRadius: 1,
        }}
        className="w-full max-w-6xl mx-auto overflow-hidden"
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'start',
            flexDirection: 'column',
            gap: 1,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              gap: 2.5,
              px: 4,
              pt: 3,
            }}
          >
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                minWidth: 80,
                borderRadius: 3,
                overflow: 'hidden',
                border: `1px solid ${theme.palette.divider}`,
                boxShadow: `0 2px 8px ${alpha(theme.palette.common.black, 0.08)}`,
              }}
            >
              <Box
                sx={{
                  width: '100%',
                  backgroundColor: theme.palette.primary.main,
                  color: theme.palette.primary.contrastText,
                  py: 0.75,
                  textAlign: 'center',
                }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    fontWeight: 600,
                    letterSpacing: 1,
                    textTransform: 'uppercase',
                    fontSize: '0.7rem',
                  }}
                >
                  {format(new Date(booking.startTime), 'EEE')}
                </Typography>
              </Box>
              <Box
                sx={{
                  py: 1,
                  px: 2,
                  textAlign: 'center',
                  backgroundColor: theme.palette.background.paper,
                }}
              >
                <Typography
                  variant="h4"
                  sx={{ fontWeight: 700, lineHeight: 1, color: theme.palette.text.primary }}
                >
                  {format(new Date(booking.startTime), 'd')}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    fontWeight: 500,
                    textTransform: 'uppercase',
                    color: theme.palette.text.secondary,
                    letterSpacing: 0.5,
                  }}
                >
                  {format(new Date(booking.startTime), 'MMM yyyy')}
                </Typography>
              </Box>
            </Box>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              <Typography variant="h5" color="primary" sx={{ fontWeight: 600 }}>
                {booking.title} ⋅ {format(new Date(booking.startTime), 'h:mm a')} -{' '}
                {format(new Date(booking.endTime), 'h:mm a')}
              </Typography>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Typography variant="body1" sx={{ fontWeight: 400 }}>
                  booking by
                </Typography>
                <Tooltip
                  title={
                    <Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', p: 0.5 }}>
                        <Phone size={16} />
                        <Typography variant="body1" sx={{ ml: 1 }}>
                          {`${booking.customer.phone}`}
                        </Typography>
                      </Box>
                      {booking.customer.email && (
                        <Box sx={{ display: 'flex', alignItems: 'center', p: 0.5 }}>
                          <Mail size={16} />
                          <Typography variant="body1" sx={{ ml: 1 }}>
                            {`${booking.customer.email}`}
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  }
                  arrow
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                    <Typography
                      variant="body1"
                      sx={{ fontWeight: 400, textDecoration: 'underline' }}
                    >
                      {booking.customer.firstName}
                    </Typography>
                    <Info size={16} color={theme.palette.text.secondary} />
                  </Box>
                </Tooltip>
              </Box>
            </Box>
          </Box>
        </Box>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'stretch',
            flexDirection: 'column',
            gap: 2,
            m: 3,
          }}
        >
          <Paper
            elevation={0}
            sx={{
              display: 'flex',
              justifyContent: 'space-evenly',
              gap: 2,
              borderRadius: 2,
              border: `1px solid ${theme.palette.divider}`,
              px: 4,
              py: 3,
            }}
          >
            <Box sx={{ flex: 1 }}>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}
              >
                Service Information
              </Typography>

              {mainService && (
                <>
                  <Typography
                    variant="h6"
                    sx={{ mb: 0, display: 'flex', flexDirection: 'column', alignItems: 'start' }}
                  >
                    {mainService.services.name}
                  </Typography>
                  {mainService.services.isNewUserService && (
                    <Chip
                      label="New User Service"
                      size="small"
                      color="primary"
                      sx={{ height: 20, mb: 1 }}
                    />
                  )}
                  <Typography variant="body2" color="text.secondary">
                    {`${mainService.services.description}`}
                  </Typography>
                  <Typography
                    variant="h6"
                    sx={{
                      my: 0.5,
                      fontWeight: 400,
                      display: 'flex',
                      gap: 0.5,
                      alignItems: 'center',
                    }}
                  >
                    <Clock size="16" style={{ color: alpha(theme.palette.text.secondary, 0.3) }} />
                    {`${mainService.services.duration} mins`}
                  </Typography>
                </>
              )}
              {addonService.length > 0 && (
                <Box>
                  {addonService.map((mapping, index) => (
                    <Box key={index}>
                      <Typography
                        variant="body1"
                        className=" flex gap-2 items-center align-middle"
                        sx={{
                          mt: 1,
                          fontSize: '1.1rem',
                          fontWeight: 500,
                        }}
                      >
                        {`${mapping.services.name}`}
                        <Chip
                          label="Add-on service"
                          size="small"
                          sx={{ gap: 0.5, borderRadius: 1 }}
                        />
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {`${mapping.services.description}`}
                      </Typography>
                      <Typography
                        variant="h6"
                        sx={{
                          mt: 0.5,
                          mb: 1.5,
                          fontSize: '1.1rem',
                          fontWeight: 400,
                          display: 'flex',
                          gap: 0.5,
                          alignItems: 'center',
                        }}
                      >
                        <Clock
                          size="16"
                          style={{ color: alpha(theme.palette.text.secondary, 0.3) }}
                        />
                        {`${mapping.services.duration} mins`}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              )}
            </Box>

            <Box sx={{ flex: 1 }}>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}
              >
                Customer Information
              </Typography>

              <Box sx={{ mb: 1 }}>
                <Typography variant="h6" sx={{ mb: 0 }}>
                  {`${booking.customer.firstName}`}
                </Typography>
              </Box>
              <InfoItem icon={Phone} label="Phone" value={booking.customer.phone} />
              <InfoItem icon={Mail} label="Email" value={booking.customer.email} />
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}
              >
                Resource Information
              </Typography>

              <Box sx={{ mb: 1 }}>
                <Typography variant="h6" sx={{ mb: 0 }}>
                  {`${booking.resource.name}`}
                </Typography>
              </Box>
              <InfoItem icon={Phone} label="Phone" value={booking.resource.phone} />
              <Typography variant="body2" color="text.secondary">
                {booking.resource.description}
              </Typography>
            </Box>
          </Paper>

          {/* Payment Information - only shown when Stripe enabled and invoice exists */}
          {isStripeEnabled && invoice && (
            <Paper
              elevation={0}
              sx={{
                borderRadius: 2,
                border: `1px solid ${theme.palette.divider}`,
                px: 4,
                py: 3,
              }}
            >
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2.5 }}>
                  <Box
                    sx={{
                      backgroundColor: alpha(theme.palette.primary.main, 0.1),
                      borderRadius: 1.5,
                      p: 1,
                      display: 'flex',
                    }}
                  >
                    <CreditCard size={18} color={theme.palette.primary.main} />
                  </Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
                    Payment Information
                  </Typography>
                </Box>

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <Box
                    sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      Invoice Number
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 600, fontFamily: 'monospace' }}>
                      {invoice.invoiceNumber}
                    </Typography>
                  </Box>

                  <Box
                    sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      Amount
                    </Typography>
                    <Typography variant="h6" color="primary" sx={{ fontWeight: 700 }}>
                      {formatCurrency(invoice.totalCents / 100, invoice.currency)}
                    </Typography>
                  </Box>

                  <Box
                    sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      Status
                    </Typography>
                    <Chip
                      label={invoice.status}
                      color={getInvoiceStatusColor(invoice.status) as any}
                      size="small"
                      sx={{ fontWeight: 600 }}
                    />
                  </Box>

                  {invoice.status === 'PAID' && invoice.paidAt && (
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                      }}
                    >
                      <Typography variant="body2" color="text.secondary">
                        Paid On
                      </Typography>
                      <Typography variant="body2" color="success.main" sx={{ fontWeight: 500 }}>
                        {new Date(invoice.paidAt).toLocaleDateString('en-AU', {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric',
                        })}
                      </Typography>
                    </Box>
                  )}

                  {/* Action Buttons */}
                  <Box
                    sx={{
                      display: 'flex',
                      gap: 1.5,
                      mt: 1,
                      pt: 2,
                      borderTop: `1px solid ${theme.palette.divider}`,
                    }}
                  >
                    <Button
                      variant="outlined"
                      startIcon={<Eye size={16} />}
                      onClick={handleViewInvoice}
                      sx={{
                        flex: 1,
                        textTransform: 'none',
                        borderRadius: 2,
                      }}
                    >
                      View
                    </Button>
                    <Button
                      variant="outlined"
                      startIcon={<Download size={16} />}
                      onClick={handleDownloadInvoice}
                      disabled={downloadProcessing}
                      sx={{
                        flex: 1,
                        textTransform: 'none',
                        borderRadius: 2,
                      }}
                    >
                      {downloadProcessing ? 'Loading...' : 'Download'}
                    </Button>
                    {invoice.status === 'UNPAID' && (
                      <Button
                        variant="contained"
                        startIcon={<Send size={16} />}
                        onClick={handleResendInvoice}
                        disabled={resendProcessing}
                        sx={{
                          flex: 1,
                          textTransform: 'none',
                          borderRadius: 2,
                        }}
                      >
                        {resendProcessing ? 'Sending...' : 'Resend'}
                      </Button>
                    )}
                    {(invoice.status === 'PAID' || invoice.status === 'PARTIAL_REFUNDED') && (
                      <Button
                        variant="outlined"
                        color="error"
                        startIcon={<RotateCcw size={16} />}
                        onClick={() => setRefundDialogOpen(true)}
                        sx={{
                          flex: 1,
                          textTransform: 'none',
                          borderRadius: 2,
                        }}
                      >
                        Refund
                      </Button>
                    )}
                  </Box>
                </Box>
              </Box>
            </Paper>
          )}

          {/* Meeting Information */}
          {booking.meetingLink && (
            <Paper
              elevation={0}
              sx={{
                borderRadius: 2,
                border: `1px solid ${theme.palette.divider}`,
                px: 4,
                py: 3,
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2.5 }}>
                <Box
                  sx={{
                    backgroundColor: alpha(theme.palette.primary.main, 0.1),
                    borderRadius: 1.5,
                    p: 1,
                    display: 'flex',
                  }}
                >
                  <Video size={18} color={theme.palette.primary.main} />
                </Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
                  Meeting Information
                </Typography>
                <Chip
                  label={booking.meetingProvider === 'zoom' ? 'Zoom' : 'Google Meet'}
                  size="small"
                  color="primary"
                  variant="outlined"
                  sx={{ ml: 'auto' }}
                />
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography
                  variant="body2"
                  component="a"
                  href={booking.meetingLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{
                    color: 'primary.main',
                    textDecoration: 'underline',
                    wordBreak: 'break-all',
                    flex: 1,
                  }}
                >
                  {booking.meetingLink}
                </Typography>
                <Tooltip title="Open in new tab">
                  <IconButton
                    size="small"
                    onClick={() => window.open(booking.meetingLink!, '_blank')}
                  >
                    <ExternalLink size={16} />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Copy link">
                  <IconButton
                    size="small"
                    onClick={() => {
                      navigator.clipboard.writeText(booking.meetingLink!);
                    }}
                  >
                    <Copy size={16} />
                  </IconButton>
                </Tooltip>
              </Box>
            </Paper>
          )}

          <Paper
            elevation={0}
            sx={
              {
                //borderRadius: 2,
                //border: `1px solid ${theme.palette.divider}`,
                // px: 4,
                // py: 3,
              }
            }
          >
            {/* Chat History */}
            <Box>
              <InfoCard
                icon={<MessageSquare size={20} color={theme.palette.primary.main} />}
                title="Conversation History"
              >
                <ChatHistory connectionId={booking.connectionId} />
              </InfoCard>
            </Box>
          </Paper>

          <Dialog
            open={recurrenceDeleteDialogOpen}
            onClose={() => setRecurrenceDeleteDialogOpen(false)}
          >
            <DialogTitle>Delete Booking</DialogTitle>
            <DialogContent>
              <Typography variant="body2" sx={{ mb: 2 }}>
                This booking is part of a recurring series. What would you like to delete?
              </Typography>
              <RadioGroup
                value={deleteOption}
                onChange={e => setDeleteOption(e.target.value as 'single' | 'following')}
              >
                <FormControlLabel value="single" control={<Radio />} label="Only this event" />
                <FormControlLabel
                  value="following"
                  control={<Radio />}
                  label="This and following events"
                />
              </RadioGroup>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setRecurrenceDeleteDialogOpen(false)}>Cancel</Button>
              <Button
                onClick={() => {
                  setRecurrenceDeleteDialogOpen(false);
                  confirmAndDelete();
                }}
                disabled={!deleteOption}
                color="error"
                variant="contained"
              >
                Delete
              </Button>
            </DialogActions>
          </Dialog>
          <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
            <DialogTitle>Delete Booking</DialogTitle>
            <DialogContent>
              <Typography variant="body2">Are you sure you want to delete this booking?</Typography>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
              <Button
                onClick={() => {
                  setDeleteDialogOpen(false);
                  confirmAndDelete();
                }}
                color="error"
                variant="contained"
              >
                Delete
              </Button>
            </DialogActions>
          </Dialog>

          {/* Invoice Detail Drawer */}
          {invoice && (
            <Drawer
              anchor="right"
              open={invoiceDetailOpen}
              onClose={() => setInvoiceDetailOpen(false)}
              PaperProps={{
                sx: {
                  width: { xs: '100%', sm: 450 },
                  p: 0,
                },
              }}
            >
              <Box
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                {/* Header */}
                <Box sx={{ p: 3, pb: 2 }}>
                  <Box
                    sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <Box
                        sx={{
                          backgroundColor: alpha(theme.palette.primary.main, 0.1),
                          borderRadius: 2,
                          p: 1.25,
                          display: 'flex',
                        }}
                      >
                        <CreditCard size={22} color={theme.palette.primary.main} />
                      </Box>
                      <Box>
                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                          Invoice Details
                        </Typography>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}
                        >
                          {invoice.invoiceNumber}
                        </Typography>
                      </Box>
                    </Box>
                    <IconButton onClick={() => setInvoiceDetailOpen(false)} size="small">
                      <X size={20} />
                    </IconButton>
                  </Box>
                </Box>

                <Divider />

                {/* Content */}
                <Box sx={{ flex: 1, overflowY: 'auto', p: 3 }}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                    {/* Status Badge */}
                    <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                      <Chip
                        label={invoice.status}
                        color={getInvoiceStatusColor(invoice.status) as any}
                        sx={{ fontWeight: 600, px: 3, py: 2.5, fontSize: '0.9rem' }}
                      />
                    </Box>

                    {/* Amount Display */}
                    <Box sx={{ textAlign: 'center', py: 2 }}>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ display: 'block', mb: 0.5 }}
                      >
                        Total Amount
                      </Typography>
                      <Typography variant="h3" color="primary" sx={{ fontWeight: 700 }}>
                        {formatCurrency(invoice.totalCents / 100, invoice.currency)}
                      </Typography>
                    </Box>

                    {/* Invoice Info */}
                    <Box
                      sx={{
                        backgroundColor: alpha(theme.palette.primary.main, 0.04),
                        borderRadius: 2,
                        p: 2.5,
                      }}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          mb: 2,
                        }}
                      >
                        <Typography variant="body2" color="text.secondary">
                          Invoice Date
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {format(new Date(invoice.createdAt), 'MMM d, yyyy')}
                        </Typography>
                      </Box>
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          mb: invoice.paidAt ? 2 : 0,
                        }}
                      >
                        <Typography variant="body2" color="text.secondary">
                          Due Date
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {format(new Date(invoice.dueDate), 'MMM d, yyyy')}
                        </Typography>
                      </Box>
                      {invoice.paidAt && (
                        <Box
                          sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                          }}
                        >
                          <Typography variant="body2" color="text.secondary">
                            Paid On
                          </Typography>
                          <Typography variant="body2" color="success.main" sx={{ fontWeight: 600 }}>
                            {format(new Date(invoice.paidAt), 'MMM d, yyyy')}
                          </Typography>
                        </Box>
                      )}
                    </Box>

                    {/* Line Items */}
                    {invoice.lineItems && invoice.lineItems.length > 0 && (
                      <Box>
                        <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
                          Line Items
                        </Typography>
                        {invoice.lineItems.map(item => (
                          <Box
                            key={item.id}
                            sx={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'flex-start',
                              py: 1.5,
                              borderBottom: `1px solid ${theme.palette.divider}`,
                            }}
                          >
                            <Box sx={{ flex: 1 }}>
                              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                {item.description}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {item.quantity} ×{' '}
                                {formatCurrency(item.unitPriceCents / 100, invoice.currency)}
                              </Typography>
                            </Box>
                            <Typography variant="body2" sx={{ fontWeight: 600, ml: 2 }}>
                              {formatCurrency(item.totalCents / 100, invoice.currency)}
                            </Typography>
                          </Box>
                        ))}
                      </Box>
                    )}

                    {/* Totals */}
                    <Box
                      sx={{
                        backgroundColor: theme.palette.action.hover,
                        borderRadius: 2,
                        p: 2.5,
                      }}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          mb: 1.5,
                        }}
                      >
                        <Typography variant="body2" color="text.secondary">
                          Subtotal
                        </Typography>
                        <Typography variant="body2">
                          {formatCurrency(invoice.subtotalCents / 100, invoice.currency)}
                        </Typography>
                      </Box>
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          mb: 1.5,
                        }}
                      >
                        <Typography variant="body2" color="text.secondary">
                          Tax
                        </Typography>
                        <Typography variant="body2">
                          {formatCurrency(invoice.taxCents / 100, invoice.currency)}
                        </Typography>
                      </Box>
                      {invoice.platformFeeCents > 0 && (
                        <Box
                          sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            mb: 1.5,
                          }}
                        >
                          <Typography variant="body2" color="text.secondary">
                            Platform Fee
                          </Typography>
                          <Typography variant="body2">
                            {formatCurrency(invoice.platformFeeCents / 100, invoice.currency)}
                          </Typography>
                        </Box>
                      )}
                      <Divider sx={{ my: 1.5 }} />
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                        }}
                      >
                        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                          Total
                        </Typography>
                        <Typography variant="h6" color="primary" sx={{ fontWeight: 700 }}>
                          {formatCurrency(invoice.totalCents / 100, invoice.currency)}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                </Box>

                {/* Footer Actions */}
                <Divider />
                <Box sx={{ p: 2.5, display: 'flex', gap: 1.5 }}>
                  <Button
                    variant="outlined"
                    startIcon={<Download size={16} />}
                    onClick={handleDownloadInvoice}
                    disabled={downloadProcessing}
                    fullWidth
                    sx={{ textTransform: 'none', borderRadius: 2, py: 1.25 }}
                  >
                    {downloadProcessing ? 'Loading...' : 'Download PDF'}
                  </Button>
                  {invoice.status === 'UNPAID' && (
                    <Button
                      variant="contained"
                      startIcon={<Send size={16} />}
                      onClick={handleResendInvoice}
                      disabled={resendProcessing}
                      fullWidth
                      sx={{ textTransform: 'none', borderRadius: 2, py: 1.25 }}
                    >
                      {resendProcessing ? 'Sending...' : 'Resend'}
                    </Button>
                  )}
                </Box>
              </Box>
            </Drawer>
          )}

          {/* Refund Dialog */}
          {invoice && (
            <RefundDialog
              open={refundDialogOpen}
              onClose={() => setRefundDialogOpen(false)}
              invoice={invoice}
              onSuccess={async () => {
                // Re-fetch the invoice to update status
                if (organization && booking.id) {
                  try {
                    const result = await paymentEndpoints.getInvoiceByBooking(
                      organization.id,
                      booking.id.toString()
                    );
                    setInvoice(result.data);
                  } catch (err) {
                    console.log('Failed to refresh invoice after refund:', err);
                  }
                }
              }}
            />
          )}
        </Box>
      </Paper>
    </>
  );
};

export default BookingDetail;
